import { ClerkProvider } from "@clerk/react"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { ThemeProvider } from "@/components/theme-provider"
import { MimirProvider } from "@/lib/mimir-client"
import { queryClient } from "@/lib/query-client"
import { router } from "@/router"
import { Toaster } from "./components/ui/sonner"

import "./index.css"

// TanStack Router calls document.startViewTransition() without attaching .ready/.finished
// handlers. If a view transition fails (e.g. duplicate view-transition-name due to
// paginated data cursor drift), Chrome rejects with InvalidStateError. Suppress that
// specific unhandled rejection so it doesn't surface as a user-visible crash.
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason instanceof DOMException &&
    event.reason.name === "InvalidStateError" &&
    event.reason.message.includes("Transition was aborted")
  ) {
    event.preventDefault()
  }
})

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE,
    sendDefaultPii: false,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  })
}

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
if (!clerkKey) {
  throw new Error("VITE_CLERK_PUBLISHABLE_KEY missing")
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider publishableKey={clerkKey} appearance={{ theme: "shadcn" }}>
      <QueryClientProvider client={queryClient}>
        <MimirProvider>
          <ThemeProvider>
            <RouterProvider router={router} />
            <Toaster position="bottom-right" />
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          </ThemeProvider>
        </MimirProvider>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
)
